import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  signal,
  WritableSignal
} from '@angular/core';
import {
  CardComponent,
  ConfirmationModalService,
  confirmationModalServiceInjectionToken,
  EFieldType,
  EPageLayout,
  ESeverity,
  ETabNavigationMode,
  IConfirmationModalService,
  IInputGroupOptions,
  InputGroupComponent,
  ITab,
  IToastService,
  ModalService,
  modalServiceInjectionToken,
  PageLayoutComponent,
  TabNavigationComponent,
  TabNavigationStateService,
  tabNavigationStateServiceInjectionToken,
  toastServiceInjectionToken
} from "@zaeper/angular-dashboard-view-lib";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { SkeletonModule } from "primeng/skeleton";
import { DialogService } from 'primeng/dynamicdialog';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ButtonModule } from "primeng/button";
import { Subscription } from 'rxjs';
import { NgClass, NgOptimizedImage } from "@angular/common";
import { ApplicationOverviewItemComponent } from "../../components/application-overview-item";
import { ActivatedRoute, Data, Router, RouterLink } from "@angular/router";
import { ICanComponentDeactivate } from "../../routing/guards/page/i-can-component-deactivate";
import { SanitizeHtmlPipe, ZTemplateDirective } from '@zaeper/angular-core-lib';
import { widgetRestClientInjectionToken } from "../../rest-clients/application/widget/widget-rest-client-injection-token";
import { IWidgetRestClient } from "../../rest-clients/application/widget/i-widget-rest.client";
import { IWidget } from "../../models/application/i-widget";
import { IImageUploadResponse, IPage } from '@zaeper/communication-lib';
import { IWidgetVersion } from "../../models/application-version/i-widget-version";

@Component( {
  selector: 'widget-detail-page',
  templateUrl: 'widget-detail-page.component.html',
  imports: [
    PageLayoutComponent,
    ZTemplateDirective,
    InputGroupComponent,
    ButtonModule,
    SkeletonModule,
    TableModule,
    NgClass,
    TabNavigationComponent,
    ApplicationOverviewItemComponent,
    CardComponent,
    NgOptimizedImage,
    SanitizeHtmlPipe,
    RouterLink
  ],
  providers: [
    {
      provide: modalServiceInjectionToken,
      useClass: ModalService
    }, {
      provide: tabNavigationStateServiceInjectionToken,
      useClass: TabNavigationStateService
    }, {
      provide: confirmationModalServiceInjectionToken,
      useClass: ConfirmationModalService
    }, DialogService
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
} )
export class WidgetDetailPageComponent implements OnInit, OnDestroy, ICanComponentDeactivate {
  protected readonly ePageLayout = EPageLayout;
  protected readonly propertiesInputGroup: IInputGroupOptions;
  protected readonly localizedInfoInputGroup: IInputGroupOptions;
  protected readonly accessControlInputGroup: IInputGroupOptions;
  protected readonly detailTabs: ITab[];
  protected readonly widgetForm: FormGroup;
  protected readonly ETabNavigationMode = ETabNavigationMode;
  protected widget$: WritableSignal<IWidget | undefined> = signal( undefined );
  protected widgetVersions$: WritableSignal<IPage<IWidgetVersion> | undefined> = signal( undefined );
  private readonly _subscriptions: Subscription;

  constructor(
    @Inject( toastServiceInjectionToken ) private readonly _toastService: IToastService,
    @Inject( widgetRestClientInjectionToken ) private readonly _widgetRestClient: IWidgetRestClient,
    @Inject(
      confirmationModalServiceInjectionToken ) private readonly _confirmationModalService: IConfirmationModalService,
    private readonly _formBuilder: FormBuilder,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router
  ) {
    this.widgetForm = this._formBuilder.group( {
      name: new FormControl( "" ),
      identifier: new FormControl( "", {
        validators: [
          Validators.minLength( 2 ),
          Validators.maxLength( 63 ),
          Validators.pattern( /^[a-z0-9]+(-[a-z0-9]+)*$/ )
        ]
      } ),
      systemApp: new FormControl( false ),
      description: new FormControl( "" )
    } );

    this.detailTabs = this._createDetailTabs();
    this.localizedInfoInputGroup = this._createLocalizedInfoInputGroup();
    this.propertiesInputGroup = this._createPropertiesInputGroup();
    this.accessControlInputGroup = this._createAccessControlInputGroup();
    this._subscriptions = new Subscription();
  }

  canDeactivate(): boolean {
    if ( !this.widgetForm.dirty ) return true;
    return confirm( "Deine Änderungen werden nicht gespeichert. Seite trotzdem verlassen?" );
  };

  ngOnInit() {
    this._subscriptions.add( this._activatedRoute.data.subscribe( ( {
                                                                      widget,
                                                                      widgetVersions
                                                                    }: Data ) => {
      this.widget$.set( widget );
      this.widgetVersions$.set( widgetVersions );

      this.widgetForm.patchValue( {
        name: widget.name,
        identifier: widget.identifier ?? "",
        systemApp: widget.systemApp ?? false,
        description: widget.description
      } );
    } ) );

    this._listenToWidgetFormChanges();
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  @HostListener( 'window:beforeunload', [ '$event' ] ) handleBeforeUnload( event: BeforeUnloadEvent ): void {
    if ( this.widgetForm.dirty ) {
      event.preventDefault();
    }
  }

  public lazyLoadTable( tableLazyLoadEvent: TableLazyLoadEvent ): Promise<void> {
    return Promise.resolve();
  }

  protected async discardChanges(): Promise<void> {
    const shouldDiscardChanges: boolean = confirm( "Entwurf löschen?" );
    if ( shouldDiscardChanges ) {
      this.widgetForm.reset();
      await this._router.navigate( [ "de", "applications" ] );
    }
  }

  protected async uploadTitleImage( event: Event ): Promise<void> {
    if ( this.widget$() === undefined ) return;

    const htmlInputElement: HTMLInputElement = <HTMLInputElement>event.target;

    if ( htmlInputElement.files && htmlInputElement.files.length ) {
      const file: File = htmlInputElement.files[ 0 ];

      try {
        const publicImageUrl: IImageUploadResponse = await this._widgetRestClient.uploadTitleImage( this.widget$()!.id, {
          titleImage: file
        } );

        this.widget$.update( ( widget: IWidget | undefined ): IWidget => {
          return {
            ...widget,
            titleImageUrl: publicImageUrl.imageUrl
          } as IWidget;
        } );

        this._toastService.setMessage( {
          title: "Erfolgreich",
          detail: "Titelbild wurde erfolgreich hochgeladen.",
          severity: ESeverity.success
        } );
      } catch ( e ) {
        this._toastService.setMessage( {
          title: "Fehlgeschlagen",
          detail: "Titelbild konnte nicht hochgeladen werden.",
          severity: ESeverity.error
        } );
      }
    }
  }

  protected async uploadIconImage( event: Event, iconType: "light" | "dark" ): Promise<void> {
    if ( this.widget$() === undefined ) return;

    const htmlInputElement: HTMLInputElement = <HTMLInputElement>event.target;

    if ( htmlInputElement.files && htmlInputElement.files.length ) {
      const file: File = htmlInputElement.files[ 0 ];

      try {
        const publicImageUrl: IImageUploadResponse = await this._widgetRestClient.uploadIconImage( this.widget$()!.id, {
          iconImage: file,
          iconType
        } );

        this.widget$.update( ( widget: IWidget | undefined ): IWidget => {
          if ( iconType === "light" ) {
            return {
              ...widget,
              lightIconUrl: publicImageUrl.imageUrl
            } as IWidget;
          } else {
            return {
              ...widget,
              darkIconUrl: publicImageUrl.imageUrl
            } as IWidget;
          }
        } );

        this._toastService.setMessage( {
          title: "Erfolgreich",
          detail: "Widget Icon wurde erfolgreich hochgeladen.",
          severity: ESeverity.success
        } );
      } catch ( e ) {
        this._toastService.setMessage( {
          title: "Fehlgeschlagen",
          detail: "Widget Icon konnte nicht hochgeladen werden.",
          severity: ESeverity.error
        } );
      }
    }
  }

  protected async update(): Promise<void> {
    const widget: IWidget | undefined = this.widget$();

    if ( widget === undefined ) return;

    try {
      await this._widgetRestClient.updateApplication( widget.id, this.widgetForm.value );
      this.widgetForm.reset( this.widgetForm.getRawValue() );

      this._toastService.setMessage( {
        title: "Erfolgreich",
        detail: "Widget wurde erfolgreich aktualisiert",
        severity: ESeverity.success
      } );
    } catch ( e ) {
      this._toastService.setMessage( {
        title: "Fehlgeschlagen",
        detail: "Widget konnte nicht aktualisiert werden",
        severity: ESeverity.error
      } );
    }
  }

  protected async publish(): Promise<void> {
    const widget: IWidget | undefined = this.widget$();

    if ( widget === undefined ) return;

    try {
      await this._widgetRestClient.publishApplication( widget.id );

      this._toastService.setMessage( {
        title: "Erfolgreich",
        detail: "Widget wurde erfolgreich veröffentlicht",
        severity: ESeverity.success
      } );
    } catch ( e ) {
      this._toastService.setMessage( {
        title: "Fehlgeschlagen",
        detail: "Widget konnte nicht veröffentlicht werden",
        severity: ESeverity.error
      } );
    }
  }

  private _listenToWidgetFormChanges() {
    this._subscriptions.add( this.widgetForm.valueChanges.subscribe( ( form ) => {
      this.widget$.set( {
        ...this.widget$()!,
        name: form.name,
        identifier: form.identifier || undefined,
        systemApp: !!form.systemApp,
        description: form.description
      } );
    } ) );
  }

  private _createDetailTabs(): ITab[] {
    return [
      {
        subject: 'English',
        title: 'English'
      }
    ];
  }

  private _createLocalizedInfoInputGroup(): IInputGroupOptions {
    return {
      groupId: "localized-info",
      children: [
        {
          fieldType: EFieldType.TEXTAREA,
          title: 'Beschreibung',
          inputId: 'description',
          placeholder: 'Beschreibung',
          required: false,
          formControlEntry: {
            formControl: this.widgetForm.get( "description" ) as FormControl<string | null>,
            key: "description"
          },
          wysiwygEditor: false
        }
      ]
    };
  }

  private _createPropertiesInputGroup(): IInputGroupOptions {
    return {
      groupId: "properties",
      title: "Eigenschaften",
      children: [
        {
          fieldType: EFieldType.TEXT,
          placeholder: "Name",
          required: true,
          formControlEntry: {
            formControl: this.widgetForm.controls[ "name" ] as FormControl<string | null>,
            key: "name"
          },
          title: "Name",
          inputId: "name"
        }, {
          fieldType: EFieldType.TEXT,
          placeholder: "e.g. my-widget",
          required: false,
          formControlEntry: {
            formControl: this.widgetForm.controls[ "identifier" ] as FormControl<string | null>,
            key: "identifier"
          },
          title: "Identifier",
          description: "URL-safe slug used in the deployment path. Lowercase alphanumeric and hyphens only.",
          inputId: "identifier"
        }, {
          fieldType: EFieldType.BOOLEAN,
          placeholder: "System app",
          required: false,
          formControlEntry: {
            formControl: this.widgetForm.controls[ "systemApp" ] as FormControl<boolean | null>,
            key: "systemApp"
          },
          title: "System app",
          description: "Marks this as a Zaeper-built system app (vs. third-party). Affects the deployment URL prefix.",
          inputId: "system-app"
        }
      ]
    };
  }

  private _createAccessControlInputGroup(): IInputGroupOptions {
    return {
      groupId: "access-control",
      title: "Freigabeeinstellungen",
      children: [
        {
          fieldType: EFieldType.BOOLEAN,
          placeholder: "Öffentlich",
          required: true,
          formControlEntry: {
            formControl: new FormControl( true ),
            key: "is-public"
          },
          title: "Öffentlich",
          description: "Diese Einstellung legt fest, ob Ihr Widget für alle im öffentlichen Store sichtbar ist oder nur für Sie bzw. ausgewählte Personen in der privaten Repository zugänglich sind.",
          inputId: "is-public"
        }
      ]
    };
  }
}
